import { DEFAULT_LOCALE, asLocale, type Locale } from "@salesboard/contracts";

/**
 * Which locale the **formatters** are currently working in.
 *
 * `lib/format.ts` is called from ordinary functions as well as from components —
 * `accessibleLabel(card)`, a column's `render`, a toast's message — so it cannot read a React
 * context, and the spec's requirement is that its call sites keep their signatures. So the
 * resolved locale is published here, once per render of the provider, and the formatters read
 * it. Every formatter also takes an explicit locale argument, which is how the unit tests
 * assert both languages without touching this.
 *
 * Module state rather than a store because there is exactly one answer per document, it
 * changes at most a few times per session, and nothing needs to re-render *because of it* —
 * the component tree re-renders because the session changed, and this is already updated by
 * then.
 */
let active: Locale = DEFAULT_LOCALE;

export function getActiveLocale(): Locale {
  return active;
}

export function setActiveLocale(locale: Locale): void {
  active = locale;
}

/** Where the last known preference is remembered, so a reload does not flash the wrong one. */
const STORAGE_KEY = "locale";

export function readStoredLocale(): Locale | undefined {
  try {
    return asLocale(localStorage.getItem(STORAGE_KEY));
  } catch {
    // Private-mode Safari throws on access. A language flash is not worth a blank page.
    return undefined;
  }
}

export function writeStoredLocale(locale: Locale): void {
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // The preference still applies for this session; only the memory of it is lost.
  }
}

/**
 * The locale to render in, in priority order.
 *
 * 1. **The session user's preference**, once it has arrived. This is the answer; everything
 *    below only covers the moments where there is no user to ask.
 * 2. **The last preference this browser saw**, so a reload paints the language the reader was
 *    already using rather than flashing another one for a frame while `/auth/me` is in flight.
 * 3. **The browser's own languages**, for a reader who has no session at all — the sign-in,
 *    invitation and reset screens. There is genuinely no preference to honour there, so
 *    following `navigator.languages` is the standard answer and the honest one; an anonymous
 *    visitor whose browser is Czech reads Czech, and one whose browser is English reads
 *    English.
 * 4. **Czech**, the product default, for anything else.
 *
 * Pure, and unit-tested, because "which language is this?" is exactly the kind of decision
 * that is impossible to check by reading a component.
 */
export function resolveLocale(input: {
  session?: Locale | null;
  stored?: Locale | null;
  languages?: readonly string[];
}): Locale {
  if (input.session) return input.session;
  if (input.stored) return input.stored;

  for (const tag of input.languages ?? []) {
    // Primary subtag only: `cs-CZ`, `cs`, and `en-GB` all answer the question.
    const primary = asLocale(tag.split("-")[0]?.toLowerCase());
    if (primary) return primary;
  }

  return DEFAULT_LOCALE;
}

/** The browser's advertised languages, defensively — jsdom and old engines vary. */
export function browserLanguages(): readonly string[] {
  if (typeof navigator === "undefined") return [];
  if (Array.isArray(navigator.languages) && navigator.languages.length > 0) return navigator.languages;
  return navigator.language ? [navigator.language] : [];
}
